import { ref } from 'vue'
import { jsonServerUrl, BACKEND_ORIGIN } from '@/config/api'
import { useMessage } from '@/mini/composables/useMessage'
import { isFreshCt } from '@/utils/cardFormUtils'
import { useAuthStore } from '@/mini/stores/auth'

// Module-level caches shared across all instances
const _blueprintsByExpansion = {}
let _cardtraderExtensions = null

function normalizePrice(value) {
  if (value == null) return null
  const normalizedValue = typeof value === 'string' ? value.replace(',', '.') : value
  const parsed = Number(normalizedValue)
  return Number.isFinite(parsed) ? parsed : null
}

async function _ctFetch(path) {
  const authStore = useAuthStore()
  const res = await authStore.authFetch(`${BACKEND_ORIGIN}/api/v1/ct/${path}`)
  if (!res.ok) throw new Error(`CT proxy error: ${res.status}`)
  return res.json()
}

async function loadBlueprintsForExpansion(expansionId) {
  if (_blueprintsByExpansion[expansionId]) return _blueprintsByExpansion[expansionId]
  try {
    const data = await _ctFetch(`blueprints/?expansion_id=${expansionId}`)
    _blueprintsByExpansion[expansionId] = Array.isArray(data) ? data : []
  } catch {
    _blueprintsByExpansion[expansionId] = []
  }
  return _blueprintsByExpansion[expansionId]
}

async function loadCardtraderExtensions() {
  if (_cardtraderExtensions) return _cardtraderExtensions
  try {
    const stored = window.localStorage.getItem('optcg.cardtrader_extensions')
    if (stored) {
      _cardtraderExtensions = JSON.parse(stored)
      return _cardtraderExtensions
    }
  } catch {}

  try {
    const data = await _ctFetch('expansions/')
    const extensions = (Array.isArray(data) ? data : [])
      .filter(e => e.game_id === 15)
      .map(({ id, name, code }) => ({ id, name, code: code?.replace(/-/g, '') }))
    window.localStorage.setItem('optcg.cardtrader_extensions', JSON.stringify(extensions))
    _cardtraderExtensions = extensions
  } catch {
    _cardtraderExtensions = []
  }
  return _cardtraderExtensions
}

export function useCardtrader(authenticatedApiFetch) {
  const {
    showInfoMessage,
    showSuccessMessage,
    showDangerMessage,
    showWarningMessage,
    showPersistentMessage,
    updateMessage,
    dismissMessage,
  } = useMessage()

  const isFetchingBlueprints = ref(false)
  const fetchBlueprintsProgress = ref({ done: 0, total: 0, errors: 0 })
  const isCheckingListing = ref(false)
  const isSelling = ref(false)
  const isSellPriceUpdating = ref(false)
  const isDeletingListing = ref(false)
  const isFetchingCtPrices = ref(false)
  const fetchCtPricesProgress = ref({ done: 0, total: 0, errors: 0 })

  /**
   * Resolve blueprint + expansion for a single card and persist to the API.
   * Mutates entity.cardtrader_blueprint and entity.cardtrader_expansion.
   * Returns { blueprintId, expansionId }.
   */
  async function fetchCardtraderDataForCard(entity) {
    const prefix = entity.card_id ? `${entity.card_id}: ` : ''
    const extensions = await loadCardtraderExtensions()
    if (!extensions.length) {
      showWarningMessage(`${prefix}No CardTrader extensions cached — go to CardBuy and save expansions first.`)
      return { blueprintId: null, expansionId: null }
    }
    const ext = extensions.find(e => e.code?.toUpperCase() === entity.set_id?.toUpperCase())
    const expansionId = ext?.id ?? null
    if (!expansionId) {
      showWarningMessage(`${prefix}No CardTrader expansion found for set "${entity.set_id}".`)
      return { blueprintId: null, expansionId: null }
    }

    let blueprintId = null
    const blueprints = await loadBlueprintsForExpansion(expansionId)
    const bp = blueprints.find(b => b.fixed_properties?.collector_number === entity.card_id)
    blueprintId = bp?.id ?? null

    await authenticatedApiFetch(`/api/v1/cards/${entity.id}/cardtrader/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ blueprint: blueprintId, expansion: expansionId }),
    })
    entity.cardtrader_blueprint = blueprintId
    entity.cardtrader_expansion = expansionId

    if (blueprintId) {
      showSuccessMessage(`${prefix}Blueprint #${blueprintId} found (expansion #${expansionId}).`)
    } else {
      showWarningMessage(`${prefix}Expansion #${expansionId} found but no blueprint matched card ID "${entity.card_id}".`)
    }
    return { blueprintId, expansionId }
  }

  /**
   * Re-fetch: given a card that already has a blueprint ID, call the marketplace API
   * to resolve the correct expansion ID and persist it.
   */
  async function refetchExpansionForCard(entity) {
    const expansionId = await fetchExpansionByBlueprint(entity.cardtrader_blueprint)
    if (!expansionId) throw new Error('No expansion found for this blueprint.')
    await authenticatedApiFetch(`/api/v1/cards/${entity.id}/cardtrader/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expansion: expansionId }),
    })
    entity.cardtrader_expansion = expansionId
    return expansionId
  }

  /**
   * Batch-fetch CardTrader blueprints for all cards in source that are missing them.
   */
  async function fetchCardtraderDataForCards(source) {
    if (isFetchingBlueprints.value) return

    const toFetch = source.filter(card => !card.cardtrader_blueprint || !card.cardtrader_expansion)
    if (!toFetch.length) {
      showInfoMessage('All CardTrader blueprints are already set.')
      return
    }

    isFetchingBlueprints.value = true
    fetchBlueprintsProgress.value = { done: 0, total: toFetch.length, errors: 0 }
    showInfoMessage(`Fetching CardTrader blueprints (${toFetch.length} cards)...`, toFetch.length * 600 + 3000)

    for (const card of toFetch) {
      try {
        await fetchCardtraderDataForCard(card)
      } catch {
        fetchBlueprintsProgress.value.errors++
        showDangerMessage(`${card.card_id} – ${card.card_name}: blueprint fetch failed`)
      }
      fetchBlueprintsProgress.value.done++
    }

    const { done, total, errors } = fetchBlueprintsProgress.value
    const msg = `CardTrader blueprints updated: ${done}/${total}${errors > 0 ? ` (${errors} error(s))` : ''}`
    errors > 0 ? showDangerMessage(msg) : showSuccessMessage(msg)
    isFetchingBlueprints.value = false
  }

  /**
   * Create a new CardTrader product listing for the given entity.
   * Mutates entity.cardtrader_product_id on success.
   */
  async function createListing(entity) {
    if (!entity.cardtrader_blueprint) {
      const prefix = entity.card_id ? `${entity.card_id}: ` : ''
      showWarningMessage(`${prefix}No CardTrader blueprint — fetch CardTrader info first.`)
      return
    }
    const price = Math.round(Number(entity.market_price) * 100) / 100
    if (!price || !Number.isFinite(price)) {
      const prefix = entity.card_id ? `${entity.card_id}: ` : ''
      showWarningMessage(`${prefix}No market price found.`)
      return
    }
    const quantity = Number(entity.sell?.quantity_to_sell ?? entity.sell_quantity_to_sell) || 1
    const prefix = entity.card_id ? `${entity.card_id}: ` : ''
    isSelling.value = true
    try {
      const res = await authenticatedApiFetch(`/api/v1/cards/${entity.id}/listing/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price, quantity }),
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(`${res.status}: ${text}`)
      }
      const data = await res.json()
      entity.cardtrader_product_id = data.product_id
      entity.cardtrader_sell_price = data.sell_price ?? price
      entity.cardtrader_sell_quantity = data.sell_quantity ?? quantity
      showSuccessMessage(`${prefix}On sale (ID: ${data.product_id}) — ${price}€ ×${quantity}`)
    } catch (err) {
      showDangerMessage(`${prefix}Error — ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      isSelling.value = false
    }
  }

  /**
   * Check whether the entity's CardTrader listing still exists.
   * Updates entity.cardtrader_sell_price (if listed) or clears entity.cardtrader_product_id.
   */
  async function checkListing(entity) {
    if (!entity.cardtrader_product_id) return
    const prefix = entity.card_id ? `${entity.card_id}: ` : ''
    isCheckingListing.value = true
    try {
      const data = await _ctFetch('products-export/')
      const product = Array.isArray(data) ? data.find(p => p.id === entity.cardtrader_product_id) : null
      if (!product) {
        await authenticatedApiFetch(`/api/v1/cards/${entity.id}/cardtrader/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_id: null }),
        })
        entity.cardtrader_product_id = null
        showInfoMessage(`${prefix}Not listed on CardTrader — sell button restored`)
      } else {
        const sellPrice = product.price_cents / 100
        const sellQuantity = product.quantity ?? null
        await authenticatedApiFetch(`/api/v1/cards/${entity.id}/cardtrader/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sell_price: sellPrice, sell_quantity: sellQuantity }),
        })
        entity.cardtrader_sell_price = sellPrice
        entity.cardtrader_sell_quantity = sellQuantity
        showInfoMessage(`${prefix}Listed at ${sellPrice}€ ×${sellQuantity} (ID: ${entity.cardtrader_product_id})`)
      }
    } catch (err) {
      showDangerMessage(`${prefix}Check error — ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      isCheckingListing.value = false
    }
  }

  /**
   * Delete the entity's CardTrader listing. Clears entity.cardtrader_product_id on success.
   */
  async function deleteListing(entity) {
    if (!entity.cardtrader_product_id) return
    const prefix = entity.card_id ? `${entity.card_id}: ` : ''
    const label = entity.card_name ? `"${entity.card_name}"` : `listing #${entity.cardtrader_product_id}`
    if (!window.confirm(`Remove ${label} from CardTrader? This cannot be undone.`)) return
    isDeletingListing.value = true
    try {
      const res = await authenticatedApiFetch(`/api/v1/cards/${entity.id}/listing/`, {
        method: 'DELETE',
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(`${res.status}: ${text}`)
      }
      entity.cardtrader_product_id = null
      showSuccessMessage(`${prefix}Listing removed from CardTrader`)
    } catch (err) {
      showDangerMessage(`${prefix}Delete failed — ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      isDeletingListing.value = false
    }
  }

  /**
   * Update the sell price and quantity of the entity's CardTrader listing.
   * Mutates entity.cardtrader_sell_price and entity.cardtrader_sell_quantity on success.
   */
  async function updateListing(entity, price, quantity) {
    const numPrice = Number(price)
    if (!numPrice || !Number.isFinite(numPrice) || numPrice <= 0) {
      const prefix = entity.card_id ? `${entity.card_id}: ` : ''
      showWarningMessage(`${prefix}Invalid price`)
      return
    }
    const numQuantity = Number(quantity)
    if (!numQuantity || !Number.isInteger(numQuantity) || numQuantity <= 0) {
      const prefix = entity.card_id ? `${entity.card_id}: ` : ''
      showWarningMessage(`${prefix}Invalid quantity`)
      return
    }
    const prefix = entity.card_id ? `${entity.card_id}: ` : ''
    isSellPriceUpdating.value = true
    try {
      const res = await authenticatedApiFetch(`/api/v1/cards/${entity.id}/listing/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: numPrice, quantity: numQuantity }),
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(`${res.status}: ${text}`)
      }
      entity.cardtrader_sell_price = numPrice
      entity.cardtrader_sell_quantity = numQuantity
      showSuccessMessage(`${prefix}Listing updated: ${numPrice}€, qty: ${numQuantity}`)
    } catch (err) {
      showDangerMessage(`${prefix}Update failed — ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      isSellPriceUpdating.value = false
    }
  }

  /**
   * Given a blueprint ID, fetch marketplace products and return the expansion id.
   * Returns expansionId (number) or null if not found.
   */
  async function fetchExpansionByBlueprint(blueprintId) {
    const data = await _ctFetch(`marketplace/?blueprint_id=${blueprintId}`)
    const products = Object.values(data)[0]
    return products?.[0]?.expansion?.id ?? null
  }

  async function saveCtPrice(card, price) {
    const fetchedPrice = normalizePrice(price)
    if (fetchedPrice == null || fetchedPrice <= 0) return

    const previousPrice = normalizePrice(card.cardtrader_market_price)
    const previousFetchedAt = card.cardtrader_market_price_fetched_at ?? null
    const storedLowestPrice = normalizePrice(card.cardtrader_lowest_market_price)
    const priceChanged = previousPrice != null && fetchedPrice !== previousPrice
    const shouldUpdateLowest = storedLowestPrice == null || fetchedPrice < storedLowestPrice
    const nowIso = new Date().toISOString()
    const patch = {
      market_price: fetchedPrice,
      market_price_fetched_at: nowIso,
      ...(priceChanged ? { prev_market_price: previousPrice } : {}),
      ...(priceChanged ? { prev_market_price_fetched_at: previousFetchedAt } : {}),
      ...(shouldUpdateLowest ? { lowest_market_price: fetchedPrice } : {}),
      ...(shouldUpdateLowest ? { lowest_market_price_fetched_at: nowIso } : {}),
    }
    await authenticatedApiFetch(`/api/v1/cards/${card.id}/cardtrader/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    card.cardtrader_market_price = fetchedPrice
    card.cardtrader_market_price_fetched_at = nowIso
    if (priceChanged) {
      card.cardtrader_prev_market_price = previousPrice
      card.cardtrader_prev_market_price_fetched_at = previousFetchedAt
    }
    if (shouldUpdateLowest) {
      card.cardtrader_lowest_market_price = fetchedPrice
      card.cardtrader_lowest_market_price_fetched_at = nowIso
    }
  }

  async function resetLowestPrice(card) {
    await authenticatedApiFetch(`/api/v1/cards/${card.id}/cardtrader/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lowest_market_price: null, lowest_market_price_fetched_at: null }),
    })
    card.cardtrader_lowest_market_price = null
    card.cardtrader_lowest_market_price_fetched_at = null
  }

  async function saveRealPrice(card, price) {
    await authenticatedApiFetch(`/api/v1/cards/${card.id}/collection/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ real_price: price }),
    })
    card.real_price = price
  }

  async function fetchCtMarketPricesForCards(source, force = false) {
    if (isFetchingCtPrices.value) return
    const toFetch = source.filter(card => card.cardtrader_blueprint && (force || !isFreshCt(card)))
    if (!toFetch.length) {
      showInfoMessage('All CT prices are already up to date (fetched within 6h).')
      return
    }
    isFetchingCtPrices.value = true
    fetchCtPricesProgress.value = { done: 0, total: toFetch.length, errors: 0 }
    const progressId = showPersistentMessage(`Fetching CT prices... 0 / ${toFetch.length}`, 'info')
    for (const card of toFetch) {
      try {
        const data = await _ctFetch(`marketplace/?blueprint_id=${card.cardtrader_blueprint}`)
        const products = Object.values(data).flat()
        const filtered = products.filter(p =>
          p.properties_hash?.condition === 'Near Mint' &&
          p.properties_hash?.onepiece_language === 'jp' &&
          p.user?.can_sell_sealed_with_ct_zero === true
        )
        if (filtered.length) {
          const price = Math.min(...filtered.map(p => p.price_cents)) / 100
          await saveCtPrice(card, price)
        }
      } catch {
        fetchCtPricesProgress.value.errors++
      }
      fetchCtPricesProgress.value.done++
      const { done, total, errors } = fetchCtPricesProgress.value
      updateMessage(progressId, `Fetching CT prices... ${done} / ${total}${errors > 0 ? ` (${errors} error(s))` : ''}`)
    }
    dismissMessage(progressId)
    const { done, total, errors } = fetchCtPricesProgress.value
    const msg = `CT prices updated: ${done}/${total}${errors > 0 ? ` (${errors} error(s))` : ''}`
    errors > 0 ? showDangerMessage(msg) : showSuccessMessage(msg)
    isFetchingCtPrices.value = false
  }

  return {
    isFetchingBlueprints,
    fetchBlueprintsProgress,
    isCheckingListing,
    isSelling,
    isSellPriceUpdating,
    isDeletingListing,
    saveCtPrice,
    resetLowestPrice,
    saveRealPrice,
    fetchCardtraderDataForCard,
    fetchCardtraderDataForCards,
    refetchExpansionForCard,
    fetchCtMarketPricesForCards,
    createListing,
    checkListing,
    deleteListing,
    updateListing,
  }
}

// Standalone: resolve blueprint + expansion for a card without patching the API
export async function resolveBlueprintForCard(setId, cardId) {
  const extensions = await loadCardtraderExtensions()
  const ext = extensions.find(e => e.code?.toUpperCase() === setId?.toUpperCase())
  const expansionId = ext?.id ?? null
  if (!expansionId) return { blueprintId: null, expansionId: null }
  const blueprints = await loadBlueprintsForExpansion(expansionId)
  const bp = blueprints.find(b => b.fixed_properties?.collector_number === cardId)
  return { blueprintId: bp?.id ?? null, expansionId }
}
